import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ClinicPayFeeRow {
  clinic_id:            string;
  clinic_name:          string;
  platform_fee_percent: number;
  last_changed_at:      string | null;
  last_changed_by:      string | null;
}

export interface UpdatePayTenantFeeInput {
  clinic_id:            string;
  platform_fee_percent: number;
}

export interface UpdatePayTenantFeeOutput {
  clinic_id: string;
  old_fee:   number;
  new_fee:   number;
}

// ── Query keys ────────────────────────────────────────────────────────────────

export const payFeeScheduleQueryKey = () => ['pay', 'fee-schedule'] as const;

// ── Hooks ─────────────────────────────────────────────────────────────────────

/**
 * Load all active clinic Pay tenants with their current platform_fee_percent
 * and the most recent audit log entry (last changed by/at).
 */
export function usePayFeeSchedule() {
  return useQuery({
    queryKey: payFeeScheduleQueryKey(),
    queryFn: async (): Promise<ClinicPayFeeRow[]> => {
      const [tenantRes, auditRes] = await Promise.all([
        supabase
          .from('clinic_pay_tenants')
          .select('clinic_id, platform_fee_percent, clinics!inner(name)')
          .not('status', 'in', '("disabled","inactive")')
          .order('clinic_id'),
        supabase
          .from('platform_fee_audit_log')
          .select('clinic_id, changed_by, changed_at')
          .order('changed_at', { ascending: false }),
      ]);

      if (tenantRes.error) throw tenantRes.error;

      // Reduce audit log to most-recent entry per clinic.
      const latestAudit = (auditRes.data ?? []).reduce<
        Record<string, { changed_by: string; changed_at: string }>
      >((acc, row) => {
        if (!acc[row.clinic_id]) {
          acc[row.clinic_id] = {
            changed_by: row.changed_by,
            changed_at: row.changed_at,
          };
        }
        return acc;
      }, {});

      return (tenantRes.data ?? []).map((row) => ({
        clinic_id:            row.clinic_id,
        clinic_name:          (row.clinics as { name: string }).name,
        platform_fee_percent: Number(row.platform_fee_percent),
        last_changed_at:      latestAudit[row.clinic_id]?.changed_at ?? null,
        last_changed_by:      latestAudit[row.clinic_id]?.changed_by ?? null,
      }));
    },
    staleTime: 30_000,
  });
}

/**
 * Mutation: call pay-update-tenant Edge Function to change a clinic's fee.
 * On success, invalidates the fee schedule query (triggers re-fetch).
 */
export function useUpdatePayTenantFee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: UpdatePayTenantFeeInput,
    ): Promise<UpdatePayTenantFeeOutput> => {
      const { data, error } = await supabase.functions.invoke<{
        ok:   boolean;
        data: UpdatePayTenantFeeOutput;
      }>('pay-update-tenant', { body: input });
      if (error) throw error;
      if (!data?.ok || !data.data) throw new Error('Fee update failed');
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: payFeeScheduleQueryKey() });
    },
  });
}
